import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Zap, Shield, Check, 
  Building2, Thermometer, Power, Wind,
  Server, Clock, Award
} from 'lucide-react';
import { facilities } from '../data/facilities';
import { QualityBadge } from '../components/QualityBadge';
import { calculateQualityScore, getMeasureXLabel } from '../utils/facilityUtils';
import { ApplyHostingButton } from '../components/hosting/ApplyHostingButton';
import { useExchangeRates } from '../utils/currencyUtils';

export function FacilityDetails() {
  const { id } = useParams<{ id: string }>();
  const facility = facilities.find(f => f.id === id);
  const { rates: exchangeRates } = useExchangeRates();

  if (!facility) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Facility Not Found
          </h1>
          <Link 
            to="/facilities"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Facilities
          </Link>
        </div>
      </div>
    );
  }

  const qualityLevel = calculateQualityScore(facility);
  const measureXLabel = getMeasureXLabel(facility.measureX);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link 
            to="/facilities"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Facilities
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="h-96 relative">
                <img 
                  src={facility.image} 
                  alt={facility.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <QualityBadge quality={qualityLevel} showDetails />
                </div>
              </div>
              
              <div className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                      {facility.name}
                    </h1>
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-5 h-5 mr-2" />
                      {facility.location}, {facility.country}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <span className={`block px-3 py-1 rounded-full text-sm font-medium ${
                      facility.securityLevel === 'enterprise' ? 'text-purple-600 bg-purple-50' :
                      facility.securityLevel === 'advanced' ? 'text-blue-600 bg-blue-50' :
                      'text-green-600 bg-green-50'
                    }`}>
                      {facility.securityLevel.charAt(0).toUpperCase() + facility.securityLevel.slice(1)} Security
                    </span>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${measureXLabel.color}`}>
                      {measureXLabel.text}
                    </div>
                  </div>
                </div>

                <p className="text-gray-600 mb-8">
                  {facility.description}
                </p>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building2 className="w-5 h-5" />
                      <span>Capacity</span>
                    </div>
                    <p className="text-xl font-semibold text-gray-900">{facility.capacity}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Zap className="w-5 h-5" />
                      <span>Power Cost</span>
                    </div>
                    <p className="text-xl font-semibold text-gray-900">${facility.powerCost}/kWh</p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Server className="w-5 h-5" />
                      <span>Min. Order</span>
                    </div>
                    <p className="text-xl font-semibold text-gray-900">{facility.minOrder} Units</p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-5 h-5" />
                      <span>Uptime</span>
                    </div>
                    <p className="text-xl font-semibold text-gray-900">{facility.uptime}%</p>
                  </div>
                </div>

                <div className="mt-8">
                  <ApplyHostingButton 
                    facility={facility}
                    currency="USD"
                    exchangeRates={exchangeRates}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Power className="w-5 h-5 text-blue-500" />
                  Power Infrastructure
                </h2>
                <div className="space-y-4">
                  {facility.powerDetails && (
                    <>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Sources</h3>
                        <div className="space-y-2">
                          {facility.powerDetails.sources.map((source: string, index: number) => (
                            <div key={index} className="flex items-center gap-2">
                              <Wind className="w-4 h-4 text-green-500" />
                              <span className="text-gray-900">{source}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Backup Power</h3>
                        <p className="text-gray-900">{facility.powerDetails.backupType}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Grid Connection</h3>
                        <p className="text-gray-900">{facility.powerDetails.gridConnection}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Thermometer className="w-5 h-5 text-blue-500" />
                  Cooling System
                </h2>
                <div className="space-y-4">
                  {facility.cooling && (
                    <>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Type</h3>
                        <p className="text-gray-900">{facility.cooling.type}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Efficiency</h3>
                        <p className="text-gray-900">{facility.cooling.efficiency}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Cooling Capacity</h3>
                        <p className="text-gray-900">{facility.cooling.capacity}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                Security & Compliance
              </h2>
              <div className="space-y-4">
                {facility.certifications.map((cert: string, index: number) => (
                  <div key={index} className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-green-500" />
                    <span className="text-gray-900">{cert}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Amenities</h2>
              <div className="space-y-2">
                {facility.amenities.map((amenity: string, index: number) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-gray-600">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>

            {facility.measureX?.ready && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">MeasureX Features</h2>
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 mb-4">
                    Version {facility.measureX.version}
                  </p>
                  {facility.measureX.features?.map((feature: string, index: number) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-blue-500" />
                      <span className="text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}